import * as React from 'react';

import { Convert } from 'core/utils';
import Button from './Button';
import { showMessage } from './Dialog';
import { useDropTarget } from './useDropTarget';
import { useOnWindowPaste } from './useOnWindowPaste';
import { Res } from 'core/api';
import type { LoadedImage } from './StyleButtonImagePickerTypes';

const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp'];

export function StyleButtonImageSelector(props: {
	onImageSelected: (image: LoadedImage) => void;
}) {
	const uploadInputRef = React.useRef<HTMLInputElement>(null);

	const openFilePicker = React.useCallback(() => {
		uploadInputRef.current?.click();
	}, []);

	const loadSelectedFile = React.useCallback(async (file: File,) => {
		if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
			void showMessage(Res.Msg.MSG_INVALID_FILE_TYPE());
			return;
		}

		try {
			const src = await Convert.fileToDataUrl(file);
			const img = await Convert.imageSrcToHTMLImage(src);
			props.onImageSelected({
				src,
				naturalWidth: img.naturalWidth || img.width,
				naturalHeight: img.naturalHeight || img.height,
			});
		} catch (e) {
			void showMessage(Res.Msg.MSG_FAILED_TO_LOAD_IMAGE(e?.toString()));
		}
	}, [props]);

	const onUploadChanged = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file)
			void loadSelectedFile(file);
	}, [loadSelectedFile]);

	const { isDragging, dragEventHandlers } = useDropTarget(dataTransfer => {
		const file = dataTransfer.files?.[0];
		if (file)
			void loadSelectedFile(file);
	});

	useOnWindowPaste(clipboardData => {
		const file = Array.from(clipboardData?.items || [])
			.find(item => item.type.startsWith('image/'))
			?.getAsFile() || null;

		if (!file)
			return;

		void loadSelectedFile(file);
	});

	return <div className='style-button-picker-selection-step'>
		<div
			tabIndex={0}
			onClick={openFilePicker}
			{...dragEventHandlers}
			className={`style-button-picker-dropzone${isDragging ? ' style-button-picker-dropzone-active' : ''}`}
		>
			<i className='fa fa-image style-button-picker-dropzone-icon' />
			<div className='style-button-picker-dropzone-text'>
				{Res.WA.WA_STYLE_BUTTON_DROPZONE_HELP_TEXT()}
			</div>
			<Button buttonStyle='info' onClick={event => { event.stopPropagation(); openFilePicker(); }}>
				{Res.WA.WA_STYLE_BUTTON_UPLOAD_BTN()}
			</Button>
			<input
				ref={uploadInputRef}
				type='file'
				accept={ACCEPTED_MIME_TYPES.join(',')}
				onChange={onUploadChanged}
				style={{ display: 'none' }}
			/>
		</div>
	</div>;
}
